/**
 * Sampling section (garment Increment 3, M4) — section 2 of the Style File.
 *
 * One sample: notes + photos, submitted for approval. Approving it advances
 * the style to PP-approved (the production gate); a Founder can override.
 * Collapsed from the earlier 5-round (Proto/Fit/SMS/PP/TOP) tracker — talking
 * to a real customer, this factory runs one sampling round, not five
 * (V1 scope cut, 2026-07-19). Photos are the generic, polymorphic
 * `attachments.*` service (a submission can carry several), reusing
 * `AttachmentsCard` rather than a bespoke single-photo upload.
 *
 * @see packages/domain/src/garment/samples
 * @see apps/web/src/features/attachments/attachments-card.tsx
 */
import { useState } from "react";
import { Check, MessageCircle, Plus, Stamp, X } from "lucide-react";
import { Button, PromptDialog, StatusPill, toast } from "@flint/ui";
import type { samples as samplesContract } from "@flint/api-contract";
import { trpc } from "../../../lib/trpc.js";
import { usePermission } from "../../../lib/permissions.js";
import { formatDate, formatDateTime } from "../../../lib/format.js";
import type { StyleStatus } from "../lib/status.js";
import { AttachmentsCard } from "../../attachments/attachments-card.js";
import { SourcingGateCard } from "./sourcing-gate-card.js";

type SamplesData = samplesContract.SamplesViewWire;

interface SamplesSectionProps {
  styleId: string;
  styleStatus: StyleStatus;
}

const STATUS_TONE: Record<SamplesData["status"], "ok" | "sky" | "bad" | "muted"> = {
  approved: "ok",
  submitted: "sky",
  rejected: "bad",
  pending: "muted",
};
const STATUS_LABEL: Record<SamplesData["status"], string> = {
  approved: "Approved",
  submitted: "Submitted",
  rejected: "Rejected",
  pending: "Not started",
};
const ICON_TONE: Record<SamplesData["status"], string> = {
  approved: "bg-emerald-50 text-emerald-600",
  submitted: "bg-sky-50 text-sky-600",
  rejected: "bg-rose-50 text-bad",
  pending: "bg-bg text-muted",
};

export function SamplesSection({ styleId, styleStatus }: SamplesSectionProps) {
  const utils = trpc.useUtils();
  const canDecide = usePermission("garment.samples.approve");
  const query = trpc.samples.getForStyle.useQuery({ styleId });
  const sourcingGate = trpc.samples.sourcingGate.useQuery({ styleId });
  const [notes, setNotes] = useState("");
  const [commentBody, setCommentBody] = useState("");
  const [overrideOpen, setOverrideOpen] = useState(false);

  const invalidate = async () => {
    await Promise.all([
      utils.samples.getForStyle.invalidate({ styleId }),
      utils.samples.sourcingGate.invalidate({ styleId }),
      utils.styles.get.invalidate({ id: styleId }),
      utils.styles.list.invalidate(),
      // Approval (or override) unlocks Production → refresh it.
      utils.production.getForStyle.invalidate({ styleId }),
      utils.auditLog.list.invalidate(),
    ]);
  };
  const onErr = (verb: string) => (e: { message: string }) => toast.error(`Couldn't ${verb}`, { description: e.message });

  const submit = trpc.samples.addSubmission.useMutation({
    onSuccess: async () => { setNotes(""); await invalidate(); toast.success("Sample submitted"); },
    onError: onErr("submit"),
  });
  const approve = trpc.samples.approve.useMutation({
    onSuccess: async (r: { styleAdvancedToPpApproved: boolean }) => { await invalidate(); toast.success("Approved", r.styleAdvancedToPpApproved ? { description: "Production is unlocked." } : {}); },
    onError: onErr("approve"),
  });
  const reject = trpc.samples.reject.useMutation({ onSuccess: async () => { await invalidate(); toast.success("Rejected", { description: "Submit a new revision when ready." }); }, onError: onErr("reject") });
  const comment = trpc.samples.addComment.useMutation({
    onSuccess: async () => { setCommentBody(""); await invalidate(); toast.success("Comment added"); },
    onError: onErr("add comment"),
  });
  const override = trpc.samples.overridePpGate.useMutation({ onSuccess: async () => { await invalidate(); toast.success("Approval overridden"); }, onError: onErr("override") });

  if (query.isLoading) return <p className="text-sm text-muted">Loading sampling…</p>;
  const data = query.data;
  if (!data) return <p className="text-sm text-muted">No sampling data.</p>;
  const busy = submit.isPending || approve.isPending || reject.isPending || comment.isPending || override.isPending;

  const accent = data.status === "approved" ? "border-emerald-200" : data.status === "rejected" ? "border-rose-200" : "border-line";
  const awaitingDecision = canDecide && data.status === "submitted" && data.latestSubmissionId;
  const canComment = data.revisionCount > 0;

  // Submit disable/title — three real states, no false-until-loaded trap.
  // Fail open on gate-query error: the server-side `assertSourcingComplete`
  // is the source of truth, so a PRECONDITION_FAILED toast beats a dead
  // button. @see docs/adr/0047-block-sampling-until-sourced.md
  let submitDisabled = false;
  let submitTitle: string | undefined;
  if (sourcingGate.isLoading) {
    submitDisabled = true;
    submitTitle = "Checking sourcing…";
  } else if (sourcingGate.isError) {
    submitDisabled = false;
    submitTitle = "Couldn't verify sourcing — server will re-check on submit.";
  } else if (sourcingGate.data?.status === "blocked") {
    submitDisabled = true;
    submitTitle = sourcingGate.data.reason ?? "Sourcing incomplete.";
  }

  return (
    <div className={`rounded-md border bg-paper p-4 ${accent}`}>
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3">
        <div className="flex min-w-0 items-center gap-3">
          <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-full ${ICON_TONE[data.status]}`} aria-hidden>
            <Stamp size={18} strokeWidth={2} />
          </span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold text-ink">Sample</span>
              {data.revisionCount > 0 ? (
                <span className="rounded-full bg-bg px-1.5 py-0.5 font-mono text-xs font-bold text-ink-2">R{data.revision}</span>
              ) : null}
              <StatusPill tone={STATUS_TONE[data.status]} size="sm" dot>{STATUS_LABEL[data.status]}</StatusPill>
            </div>
            <div className="mt-0.5 text-xs text-muted">
              {data.submittedAt ? `Submitted ${formatDate(data.submittedAt)}` : "Not submitted yet"}
            </div>
          </div>
        </div>

        {!data.ppApproved && styleStatus === "costing_locked" ? (
          <button
            type="button"
            disabled={busy}
            title="Founder only — logs an audit reason"
            onClick={() => setOverrideOpen(true)}
            className="shrink-0 text-xs font-medium text-amber-600 hover:underline disabled:opacity-40"
          >
            Override approval →
          </button>
        ) : null}
      </div>

      {/* The decision — big, focused, impossible to miss when it's waiting on you. */}
      {awaitingDecision ? (
        <div className="mt-3.5 flex flex-wrap items-center justify-between gap-3 rounded-md border border-sky-100 bg-sky-50/50 px-4 py-3">
          <span className="text-sm font-medium text-sky-900">This sample is awaiting your decision.</span>
          <div className="flex shrink-0 gap-2.5">
            <Button
              size="lg"
              disabled={busy}
              onClick={() => approve.mutate({ submissionId: data.latestSubmissionId! })}
              className="bg-emerald-600 text-paper hover:bg-emerald-700"
            >
              <Check size={17} /> Approve
            </Button>
            <Button
              size="lg"
              intent="danger"
              disabled={busy}
              onClick={() => reject.mutate({ submissionId: data.latestSubmissionId! })}
            >
              <X size={17} /> Reject
            </Button>
          </div>
        </div>
      ) : null}

      {/* Sourcing gate — sampling is blocked until every non-process BOM line is
          ready or issued. Renders nothing when sourcing is complete. Query is
          fetched here (once) and unpacked into the child props — TanStack
          dedupes, but one subscription keeps the loading/error states
          unambiguous. */}
      <SourcingGateCard
        styleId={styleId}
        data={sourcingGate.data}
        isError={sourcingGate.isError}
        refetch={() => void sourcingGate.refetch()}
      />

      {/* Notes + submit — always editable; submitting starts a new revision. */}
      <div className="mt-3.5 flex flex-col gap-2">
        <textarea
          value={notes}
          onChange={(e) => { const v = e.currentTarget.value; setNotes(v); }}
          placeholder="Notes for this sample…"
          rows={2}
          className="resize-none rounded-md border border-line bg-bg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-line"
        />
        <Button
          size="sm"
          disabled={busy || submitDisabled}
          title={submitTitle}
          onClick={() => submit.mutate({ styleId, notes: notes.trim() || null })}
          className="self-start"
        >
          <Plus size={13} /> {data.revisionCount > 0 ? "Submit new revision" : "Submit"}
        </Button>
      </div>

      {/* Photos — the generic attachments service; several photos per sample. */}
      <div className="mt-4">
        <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted">Photos</h4>
        {data.latestSubmissionId ? (
          <AttachmentsCard
            ownerType="garment_sample_submission"
            ownerId={data.latestSubmissionId}
            emptyLabel="No photos yet."
            addLabel="Add photos"
          />
        ) : (
          <p className="rounded-md border border-dashed border-line-2 bg-paper p-4 text-center text-sm text-muted">
            Submit the sample first, then attach photos.
          </p>
        )}
      </div>

      {/* Comments — always visible, with a persistent composer (not hidden behind a toggle). */}
      <div className="mt-4">
        <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted">
          Comments{data.commentCount > 0 ? ` (${data.commentCount})` : ""}
        </h4>
        <div className="flex flex-col gap-2 rounded-md border border-line bg-bg/40 p-3">
          {data.comments.length > 0 ? (
            <div className="flex max-h-56 flex-col gap-2.5 overflow-y-auto pr-1">
              {data.comments.map((c, i) => (
                <CommentRow key={i} authorKind={c.authorKind} body={c.body} at={c.at} />
              ))}
            </div>
          ) : (
            <p className="text-xs text-muted">No comments yet.</p>
          )}
          {canComment ? (
            <div className="flex items-end gap-2 border-t border-line pt-2.5">
              <textarea
                value={commentBody}
                onChange={(e) => { const v = e.currentTarget.value; setCommentBody(v); }}
                placeholder="Buyer feedback…"
                rows={1}
                className="min-w-0 flex-1 resize-none rounded-md border border-line bg-paper px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-line"
              />
              <Button
                size="sm"
                disabled={busy || commentBody.trim() === ""}
                onClick={() => comment.mutate({ styleId, submissionId: data.latestSubmissionId, body: commentBody.trim() })}
              >
                <MessageCircle size={13} /> Post
              </Button>
            </div>
          ) : null}
        </div>
      </div>

      <PromptDialog
        open={overrideOpen}
        onOpenChange={setOverrideOpen}
        title="Override the sample approval?"
        description="Founder-only. Moves the style to PP-approved without an approved sample — the reason is written to the audit log."
        label="Reason"
        placeholder="e.g. Buyer waived sampling on email — approved verbally"
        intent="danger"
        multiline
        confirmLabel="Override approval"
        pending={override.isPending}
        onConfirm={(reason) => override.mutate({ styleId, reason })}
      />
    </div>
  );
}

function CommentRow({ authorKind, body, at }: { authorKind: string; body: string; at: string }) {
  return (
    <div className="flex items-start gap-2.5 rounded-md bg-paper px-2.5 py-2 text-xs shadow-sm">
      <span
        className={`mt-px shrink-0 rounded-full px-1.5 py-0.5 text-xs font-semibold capitalize ${
          authorKind === "buyer" ? "bg-sky-50 text-sky-700" : "bg-bg text-ink-2"
        }`}
      >
        {authorKind}
      </span>
      <span className="min-w-0 flex-1 whitespace-pre-wrap break-words text-ink-2">{body}</span>
      <span className="shrink-0 text-xs text-muted">{formatDateTime(at)}</span>
    </div>
  );
}
